import { FC, useEffect, useMemo, useState } from 'react';
import styles from './collectionsComponent.module.scss';
import producersController from '../../../../../Api/producersController';
import collectionsController, { IAddCollectionDto } from '../../../../../Api/collectionsController';
import ListPartWithEditForm from './Components/ListPartWithEditForm';
import { ICollection } from '../../../../../interfaces/ICollection';
import { IProducer } from '../../../../../interfaces/IProducer';
import CsvDownloadButton from 'react-json-to-csv';
import { useUser } from '../../../../../hooks/useUser';

const CollectionsComponent: FC = () => {
    const user = useUser();
    const [collectionsData, setCollectionsData] = useState<ICollection[]>([]);
    const [producersData, setProducersData] = useState<IProducer[]>([]);
    const [showAddForm, setShowAddForm] = useState(false);
    const [query, setQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [name, setName] = useState('');
    const [producer, setProducer] = useState('');
    const [image, setImage] = useState<any>('');

    const loadData = async () => {
        setLoading(true); setError('');
        try {
            const [collections, producers] = await Promise.all([
                collectionsController.getAllCollections(),
                producersController.getAllProducers(),
            ]);
            setCollectionsData(collections.collections || []);
            setProducersData(producers.producers || []);
            setProducer((current) => current || producers.producers?.[0]?._id || '');
        } catch {
            setError('Nie udało się pobrać kolekcji.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); }, []);

    const producerNames = useMemo(() => Object.fromEntries(producersData.map((item) => [item._id, item.name])), [producersData]);
    const filteredCollections = useMemo(() => {
        const phrase = query.trim().toLocaleLowerCase('pl');
        if (!phrase) return collectionsData;
        return collectionsData.filter((item) => `${item.name || ''} ${producerNames[item.producer] || ''}`.toLocaleLowerCase('pl').includes(phrase));
    }, [collectionsData, producerNames, query]);

    const addCollection = async () => {
        if (!name.trim() || !producer || !image) {
            setError('Podaj nazwę, producenta i wybierz zdjęcie kolekcji.');
            return;
        }
        setError('');
        try {
            const payload: IAddCollectionDto = { name: name.trim().toUpperCase(), producer, image };
            const res = await collectionsController.addCollection(payload);
            if (res.collection) setCollectionsData((current) => [...current, res.collection as ICollection]);
            setName(''); setImage(''); setShowAddForm(false);
        } catch {
            setError('Nie udało się dodać kolekcji.');
        }
    };

    const editCollection = async (collection: ICollection) => {
        const res = await collectionsController.editCollection(collection);
        setCollectionsData((current) => current.map((item) => item._id === collection._id ? (res.updatedCollection || item) : item));
    };

    const deleteCollection = async (_id: string) => {
        if (!window.confirm('Usunąć tę kolekcję?')) return;
        await collectionsController.deleteCollection(_id);
        setCollectionsData((current) => current.filter((item) => item._id !== _id));
    };

    const exportData = collectionsData.map(({ __v, ...collection }) => collection);

    return <div className={styles.general}>
        <div className={styles.moduleHeader}>
            <div><h2>Kolekcje</h2><p>Porządkuj kolekcje według producenta i zarządzaj zdjęciami aranżacji.</p></div>
            <span className={styles.counter}>{collectionsData.length} kolekcji</span>
        </div>
        <div className={styles.toolbar}>
            <input aria-label="Szukaj kolekcji" placeholder="Szukaj kolekcji lub producenta…" value={query} onChange={(event) => setQuery(event.target.value)} />
            <div className={styles.actions}>
                <CsvDownloadButton data={exportData} filename="kolekcje.csv">Eksportuj CSV</CsvDownloadButton>
                <button type="button" onClick={loadData}>Odśwież</button>
                {user.hasPermission('collections.create') && <button type="button" className={styles.primaryButton} onClick={() => setShowAddForm((current) => !current)}>{showAddForm ? 'Anuluj' : 'Dodaj kolekcję'}</button>}
            </div>
        </div>
        {error && <div className={styles.error}>{error}</div>}
        {showAddForm && <div className={styles.formContainer}>
            <label>Nazwa kolekcji<input type="text" value={name} onChange={(event) => setName(event.target.value)} /></label>
            <label>Producent<select value={producer} onChange={(event) => setProducer(event.target.value)}>{producersData.map((item) => <option key={item._id} value={item._id}>{item.name}</option>)}</select></label>
            <label>Zdjęcie aranżacji<input type="file" accept="image/*" onChange={(event) => setImage(event.target.files?.[0] || '')} /></label>
            <button type="button" className={styles.primaryButton} onClick={addCollection}>Zapisz kolekcję</button>
        </div>}
        <div className={styles.listHeader}><span>Zdjęcie</span><span>Kolekcja</span><span>Producent</span><span>Akcje</span></div>
        {loading ? <div className={styles.state}>Pobieranie kolekcji…</div> : filteredCollections.length === 0 ? <div className={styles.state}>Brak kolekcji pasujących do wyszukiwania.</div> : <div className={styles.collectionsData}>
            {filteredCollections.map((collection) => <ListPartWithEditForm key={collection._id} {...collection} producers={producersData} onDelete={deleteCollection} onEdit={editCollection} canEdit={user.hasPermission('collections.update')} canDelete={user.hasPermission('collections.delete')} />)}
        </div>}
    </div>;
};

export default CollectionsComponent;
